/*
 * The treatment subcategory subheadings' data, owned in one place.
 *
 * Two surfaces render these headings - the search results page and the Full Product Category
 * listing section - and both derive them from the cards already on screen rather than fetching
 * the subcategory list, so a subcategory with nothing bookable at the chosen spa produces no
 * heading and there is no second list to fall out of step with. Sharing the rule is what stops
 * the two drifting apart, exactly as shared/treatmentCategories.js does for the chip row above
 * them.
 *
 * See docs/product-ordering.md, "Treatment subcategories: a second level of ordering".
 */

/**
 * A card list split into subcategory groups, or null to render one flat list.
 *
 * Callers narrow to a single treatment category before calling, and null is the normal case:
 * subheadings belong to one category at a time, because under "All Treatments" a heading
 * reading "Manicures" would be describing a handful of cards partway down a far longer list.
 *
 * Ordering: the groups by `subcategory_order` (the category's Subcategories tab), with name as
 * the tiebreak, as it is for chips. The cards within each keep the order they arrive in, which
 * is ProductSearchService::search()'s - re-sorting them here would undo gift mode's price order.
 *
 * Cards with no subcategory go last, under "Other {category}", and that group is omitted when
 * empty. A category whose visible cards are ALL unsubcategorised produces no groups at all
 * rather than a single "Other" heading over everything - a heading that tells nobody anything.
 */
export function subcategoryGroupsIn(cards) {
    if (! cards.some(card => card.subcategory_id && card.subcategory_name)) return null;

    const groups = new Map();
    const other  = [];

    cards.forEach((card) => {
        // Reachable for a real treatment sitting outside its category's subcategories, and for
        // every package filed under a treatment category - those carry the category keys but
        // never a subcategory (ProductPayloadService::forPackage).
        if (! card.subcategory_id || ! card.subcategory_name) {
            other.push(card);

            return;
        }

        const group = groups.get(card.subcategory_id);

        group
            ? group.products.push(card)
            : groups.set(card.subcategory_id, {
                id: card.subcategory_id,
                name: card.subcategory_name,
                order: card.subcategory_order ?? 0,
                products: [card],
            });
    });

    const sorted = [...groups.values()]
        .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

    if (other.length) {
        // The category's own name comes off the cards rather than being passed in, for the
        // same reason the chip labels do - the payload already carries it.
        sorted.push({
            id: null,
            name: `Other ${other[0].category_name ?? 'treatments'}`,
            order: Infinity,
            products: other,
        });
    }

    return sorted;
}
